/**
 * Rozgari (Jivika) — Community job board.
 *
 * Any approved member (consumer OR vendor) can post a job opening.
 * Posts auto-expire after 30 days and can be renewed. Contact info
 * (phone / WhatsApp / email) is stored on the post so candidates
 * reach out directly. No formal application flow.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Briefcase, Plus, Search, MapPin, Building2, Clock, Wallet, Send,
  Share2, Copy, MessageCircle, Phone, Mail, Trash2, RefreshCw, Loader2, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const APP_ID = "rozgari";

const initialForm = {
  title: "",
  company: "",
  category: "",
  employment_type: "",
  city: "",
  description: "",
  salary_min: "",
  salary_max: "",
  salary_period: "monthly",
  experience: "",
  work_mode: "",
  languages: "",
  contact_phone: "",
  contact_whatsapp: "",
  contact_email: "",
  expiry_days: 30,
};

export default function Rozgari() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [cities, setCities] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    city: searchParams.get("city") || "",
    category: searchParams.get("category") || "",
    employment_type: searchParams.get("employment_type") || "",
    work_mode: searchParams.get("work_mode") || "",
    search: searchParams.get("search") || "",
  });

  const [postOpen, setPostOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(null); // job object or null
  const [mineOpen, setMineOpen] = useState(false);

  // -------- Bootstrap meta + cities
  useEffect(() => {
    api.get("/jobs/meta").then((r) => setMeta(r.data)).catch(() => setMeta(null));
    api.get("/cities").then((r) => setCities(r.data || [])).catch(() => setCities([]));
  }, []);

  // -------- Fetch jobs when filters change
  useEffect(() => {
    let stop = false;
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/jobs", { params })
      .then((r) => {
        if (stop) return;
        setJobs(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => { if (!stop) { setJobs([]); setTotal(0); }})
      .finally(() => { if (!stop) setLoading(false); });
    // reflect in URL for shareability
    const usp = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) usp.set(k, v); });
    setSearchParams(usp, { replace: true });
    return () => { stop = true; };
  }, [filters]);

  // -------- Deep-link to a specific job via ?job=<id>
  useEffect(() => {
    const id = searchParams.get("job");
    if (id) {
      api.get(`/jobs/${id}`).then((r) => setDetailOpen(r.data)).catch(() => {});
    }
  }, [searchParams]);

  const clearFilter = (k) => setFilters((f) => ({ ...f, [k]: "" }));
  const filterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-background" data-testid={`app-${APP_ID}-root`}>
      {/* Hero */}
      <div
        className="relative"
        style={{
          background: "linear-gradient(135deg,#3b0764 0%, #6d28d9 45%, #f97316 100%)",
        }}
        data-testid={`app-${APP_ID}-hero`}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(20,10,40,0.35), rgba(20,10,40,0.05))" }} />
        <div className="relative mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-12 py-12 sm:py-16 text-white">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs">
            <Briefcase className="h-3.5 w-3.5" /> Rozgari · Jivika — Jobs shared by our community
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">Rozgari</h1>
          <p className="mt-2 max-w-xl text-sm sm:text-base opacity-90">
            जीविका — Discover openings shared by fellow members, or share one that&apos;s worth someone else&apos;s time.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row items-stretch gap-2 max-w-3xl">
            <div className="flex items-center gap-2 rounded-full bg-white/95 text-neutral-800 px-4 py-2 flex-1">
              <Search className="h-4 w-4" />
              <Input
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                placeholder="Search: backend developer, tiffin cook, priest…"
                className="border-0 focus-visible:ring-0 h-8 p-0 bg-transparent"
                data-testid={`app-${APP_ID}-search`}
              />
              <select
                value={filters.city}
                onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
                className="bg-transparent text-sm outline-none border-l border-neutral-300 pl-3"
                data-testid={`app-${APP_ID}-city`}
              >
                <option value="">All cities</option>
                {cities.map((c) => (
                  <option key={c.slug || c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => setPostOpen(true)}
              className="rounded-full h-11 px-5 bg-white text-purple-900 hover:bg-white/90"
              data-testid={`app-${APP_ID}-post-btn`}
            >
              <Plus className="h-4 w-4 mr-1" /> Post a job
            </Button>
            {user && (
              <Button
                onClick={() => setMineOpen(true)}
                variant="ghost"
                className="rounded-full h-11 text-white hover:bg-white/15"
                data-testid={`app-${APP_ID}-mine-btn`}
              >
                My posts
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Filter row */}
      <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-12 pt-6">
        <div className="flex flex-wrap items-center gap-2" data-testid={`app-${APP_ID}-filter-row`}>
          <FilterSelect
            id="category"
            value={filters.category}
            options={meta?.categories || []}
            onChange={(v) => setFilters((f) => ({ ...f, category: v }))}
            label="Category"
          />
          <FilterSelect
            id="employment_type"
            value={filters.employment_type}
            options={meta?.employment_types || []}
            onChange={(v) => setFilters((f) => ({ ...f, employment_type: v }))}
            label="Type"
          />
          <FilterSelect
            id="work_mode"
            value={filters.work_mode}
            options={meta?.work_modes || []}
            onChange={(v) => setFilters((f) => ({ ...f, work_mode: v }))}
            label="Work mode"
          />
          {filterCount > 0 && (
            <button
              type="button"
              onClick={() => setFilters({ city: "", category: "", employment_type: "", work_mode: "", search: "" })}
              className="text-xs text-muted-foreground hover:text-foreground"
              data-testid={`app-${APP_ID}-filter-clear`}
            >
              Clear filters ({filterCount})
            </button>
          )}
          <div className="ml-auto text-xs text-muted-foreground" data-testid={`app-${APP_ID}-count`}>
            {loading ? "Loading…" : `${total} active ${total === 1 ? "post" : "posts"}`}
          </div>
        </div>
      </div>

      {/* Listing */}
      <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-12 py-8">
        {loading && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-40 rounded-xl bg-muted/40 animate-pulse" />
            ))}
          </div>
        )}
        {!loading && jobs.length === 0 && (
          <Card className="p-10 text-center" data-testid={`app-${APP_ID}-empty`}>
            <Briefcase className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <div className="font-medium">No matching jobs right now</div>
            <p className="text-sm text-muted-foreground mt-1">
              Be the first — <button className="text-primary underline" onClick={() => setPostOpen(true)}>post an opening</button> or share this page with your network.
            </p>
          </Card>
        )}
        {!loading && jobs.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid={`app-${APP_ID}-grid`}>
            {jobs.map((j) => (
              <JobCard key={j.job_id} job={j} onOpen={() => setDetailOpen(j)} />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {postOpen && (
        <PostJobDialog
          meta={meta}
          cities={cities}
          onClose={() => setPostOpen(false)}
          onCreated={(created) => {
            setPostOpen(false);
            setJobs((prev) => [created, ...prev]);
            setTotal((t) => t + 1);
            setDetailOpen(created);
          }}
        />
      )}
      {detailOpen && (
        <JobDetailDialog
          job={detailOpen}
          onClose={() => setDetailOpen(null)}
          onDeleted={(id) => {
            setDetailOpen(null);
            setJobs((prev) => prev.filter((x) => x.job_id !== id));
            setTotal((t) => Math.max(0, t - 1));
          }}
        />
      )}
      {mineOpen && (
        <MyJobsDialog
          onClose={() => setMineOpen(false)}
          onOpen={(j) => { setMineOpen(false); setDetailOpen(j); }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                     */
/* ------------------------------------------------------------------ */

function FilterSelect({ id, label, value, options, onChange }) {
  return (
    <div className="flex items-center gap-1">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`text-xs rounded-full px-3 py-1.5 border ${value ? "border-primary/60 bg-primary/10 text-primary font-medium" : "border-border/70 bg-background text-foreground/80"}`}
        data-testid={`app-${APP_ID}-filter-${id}`}
      >
        <option value="">{label}: any</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="text-muted-foreground hover:text-foreground"
          aria-label={`Clear ${label}`}
          data-testid={`app-${APP_ID}-filter-${id}-clear`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function JobCard({ job, onOpen }) {
  const created = job.created_at ? new Date(job.created_at) : null;
  const days = created ? Math.floor((Date.now() - created.getTime()) / 86400000) : null;
  const salary = formatSalary(job);
  const status = job.status || "active";
  const isExpired = status === "expired";
  const isClosed = status === "closed";
  return (
    <Card
      className={`p-4 cursor-pointer hover:border-primary/40 hover:shadow-md transition-all ${(isExpired || isClosed) ? "opacity-70" : ""}`}
      onClick={onOpen}
      data-testid={`app-${APP_ID}-card-${job.job_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.15em] text-primary font-semibold flex items-center gap-1.5">
            <Briefcase className="h-3 w-3" /> {job.category}
          </div>
          <div className="font-heading text-lg leading-snug mt-1 line-clamp-2">{job.title}</div>
          {job.company && (
            <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
              <Building2 className="h-3 w-3" /> {job.company}
            </div>
          )}
        </div>
        <StatusPill status={status} daysLeft={job.days_left} />
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        <Chip icon={MapPin} label={job.city} />
        <Chip icon={Clock} label={job.employment_type} />
        {job.work_mode && <Chip label={job.work_mode} />}
        {job.experience && <Chip label={job.experience} />}
      </div>
      <div className="flex items-end justify-between mt-3 pt-3 border-t border-border/60">
        <div className="text-xs">
          {salary ? (
            <div className="flex items-center gap-1 font-medium text-foreground/90">
              <Wallet className="h-3.5 w-3.5 text-emerald-600" /> {salary}
            </div>
          ) : (
            <span className="text-muted-foreground italic">Salary negotiable</span>
          )}
        </div>
        {days !== null && (
          <div className="text-[10px] text-muted-foreground">
            {days === 0 ? "posted today" : days === 1 ? "1 day ago" : `${days} days ago`}
          </div>
        )}
      </div>
    </Card>
  );
}

function StatusPill({ status, daysLeft }) {
  if (status === "expired") {
    return (
      <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300" data-testid="job-status-expired">
        Expired
      </span>
    );
  }
  if (status === "closed") {
    return (
      <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200" data-testid="job-status-closed">
        Closed
      </span>
    );
  }
  const soon = typeof daysLeft === "number" && daysLeft <= 3;
  return (
    <span
      className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${soon ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"}`}
      data-testid={soon ? "job-status-active-soon" : "job-status-active"}
    >
      {soon ? `Expires in ${daysLeft}d` : "Active"}
    </span>
  );
}

function Chip({ icon: Icon, label }) {
  if (!label) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted/70 border border-border/60 px-2 py-0.5 text-[11px] text-foreground/80">
      {Icon && <Icon className="h-3 w-3 opacity-70" />}
      {label}
    </span>
  );
}

function formatSalary(j) {
  const min = j.salary_min;
  const max = j.salary_max;
  const period = j.salary_period || "monthly";
  if (period === "negotiable" || (!min && !max)) return null;
  const fmt = (n) => new Intl.NumberFormat("en-IN").format(n);
  if (min && max) return `₹${fmt(min)}–₹${fmt(max)} / ${period}`;
  if (min) return `From ₹${fmt(min)} / ${period}`;
  if (max) return `Up to ₹${fmt(max)} / ${period}`;
  return null;
}

/* --------- Post Job dialog --------- */

function PostJobDialog({ meta, cities, onClose, onCreated }) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title || form.title.length < 3) return toast.error("Add a job title");
    if (!form.category) return toast.error("Pick a category");
    if (!form.employment_type) return toast.error("Pick an employment type");
    if (!form.city) return toast.error("Add a city");
    if (!form.description || form.description.length < 20) return toast.error("Description needs at least 20 characters");
    if (!form.contact_phone && !form.contact_whatsapp && !form.contact_email) {
      return toast.error("Add at least one contact — phone / WhatsApp / email");
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Coerce numeric fields
      ["salary_min", "salary_max", "expiry_days"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
        else payload[k] = Number(payload[k]);
      });
      // Empty strings → drop
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      if (payload.languages && typeof payload.languages === "string") {
        payload.languages = payload.languages.split(",").map((s) => s.trim()).filter(Boolean);
      }
      const { data } = await api.post("/jobs", payload);
      toast.success("Job posted — live for 30 days");
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post — try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`app-${APP_ID}-post-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" /> Post a new job
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Your name and email are auto-attached from your profile so applicants can trust the source. Post stays live for 30 days.
        </p>
        <div className="grid gap-3 mt-3">
          <Field label="Title *">
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Senior backend developer (Python/Node)" data-testid={`app-${APP_ID}-form-title`} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Company / business">
              <Input value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="e.g. LearnIT TechIndia" data-testid={`app-${APP_ID}-form-company`} />
            </Field>
            <Field label="City *">
              <select
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid={`app-${APP_ID}-form-city`}
              >
                <option value="">Pick a city…</option>
                {cities.map((c) => <option key={c.slug || c.name} value={c.name}>{c.name}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Category *">
              <select value={form.category} onChange={(e) => set("category", e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid={`app-${APP_ID}-form-category`}>
                <option value="">Pick a category…</option>
                {(meta?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Employment type *">
              <select value={form.employment_type} onChange={(e) => set("employment_type", e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid={`app-${APP_ID}-form-employment_type`}>
                <option value="">Pick a type…</option>
                {(meta?.employment_types || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Work mode">
              <select value={form.work_mode} onChange={(e) => set("work_mode", e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid={`app-${APP_ID}-form-work_mode`}>
                <option value="">Any</option>
                {(meta?.work_modes || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Experience">
              <select value={form.experience} onChange={(e) => set("experience", e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid={`app-${APP_ID}-form-experience`}>
                <option value="">Any</option>
                {(meta?.experience_buckets || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Languages">
              <Input value={form.languages} onChange={(e) => set("languages", e.target.value)} placeholder="e.g. Marathi, English" data-testid={`app-${APP_ID}-form-languages`} />
            </Field>
          </div>
          <Field label="Description * (min 20 chars)">
            <Textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Responsibilities, must-haves, nice-to-haves, joining timeline…" data-testid={`app-${APP_ID}-form-description`} />
          </Field>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Salary min (₹)">
              <Input type="number" value={form.salary_min} onChange={(e) => set("salary_min", e.target.value)} placeholder="30000" data-testid={`app-${APP_ID}-form-salary_min`} />
            </Field>
            <Field label="Salary max (₹)">
              <Input type="number" value={form.salary_max} onChange={(e) => set("salary_max", e.target.value)} placeholder="60000" data-testid={`app-${APP_ID}-form-salary_max`} />
            </Field>
            <Field label="Period">
              <select value={form.salary_period} onChange={(e) => set("salary_period", e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid={`app-${APP_ID}-form-salary_period`}>
                {(meta?.salary_periods || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Post stays live for">
            <div className="flex flex-wrap gap-2" data-testid={`app-${APP_ID}-form-expiry-group`}>
              {(meta?.expiry_options || [7, 14, 30, 60, 90]).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => set("expiry_days", d)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    Number(form.expiry_days) === d
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-border/70 text-foreground/80 hover:bg-muted"
                  }`}
                  data-testid={`app-${APP_ID}-form-expiry-${d}`}
                >
                  {d} days
                </button>
              ))}
              <span className="text-[11px] text-muted-foreground self-center ml-1">
                Auto-archived when it expires — you can renew any time before or after.
              </span>
            </div>
          </Field>
          <div className="rounded-lg border border-border/60 p-3 bg-muted/30">
            <div className="text-xs uppercase tracking-[0.14em] text-primary font-semibold mb-2">Contact — at least one *</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Phone">
                <Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="+91…" data-testid={`app-${APP_ID}-form-phone`} />
              </Field>
              <Field label="WhatsApp">
                <Input value={form.contact_whatsapp} onChange={(e) => set("contact_whatsapp", e.target.value)} placeholder="+91…" data-testid={`app-${APP_ID}-form-whatsapp`} />
              </Field>
              <Field label="Email">
                <Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="you@example.com" data-testid={`app-${APP_ID}-form-email`} />
              </Field>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid={`app-${APP_ID}-form-submit`}>
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Posting…</> : <><Send className="h-4 w-4 mr-1" /> Post job</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-1">{label}</div>
      {children}
    </label>
  );
}

/* --------- Job detail dialog with share + delete/renew for owners --------- */

function JobDetailDialog({ job: initial, onClose, onDeleted }) {
  const { user } = useAuth();
  const [job, setJob] = useState(initial);
  const [meta, setMeta] = useState(null);
  const [renewDays, setRenewDays] = useState(30);
  const isOwner = user && user.user_id === job.user_id;
  const isAdmin = user && (user.role === "admin" || user.role === "superadmin");
  const canManage = isOwner || isAdmin;
  const [busy, setBusy] = useState(false);
  const shareUrl = `${window.location.origin}/apps/rozgari?job=${job.job_id}`;
  const salary = formatSalary(job);

  useEffect(() => {
    if (canManage && !meta) {
      api.get("/jobs/meta").then((r) => setMeta(r.data)).catch(() => {});
    }
  }, [canManage, meta]);

  const share = async () => {
    const text = `Job: ${job.title}${job.company ? " @ " + job.company : ""} in ${job.city} — via sanskariBazarIn Rozgari`;
    if (navigator.share) {
      try { await navigator.share({ title: job.title, text, url: shareUrl }); return; } catch { /* cancelled */ }
    }
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };
  const whatsapp = () => {
    const text = encodeURIComponent(`Job: *${job.title}*${job.company ? " @ " + job.company : ""}, ${job.city}\n${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };
  const renew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.job_id}/renew`, null, { params: { days: renewDays } });
      setJob(data);
      toast.success(`Renewed for ${renewDays} more days`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Renew failed");
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this job post? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/jobs/${job.job_id}`);
      toast.success("Job post deleted");
      onDeleted(job.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const expires = job.expires_at ? new Date(job.expires_at) : null;
  const daysLeft = typeof job.days_left === "number" ? job.days_left : (expires ? Math.ceil((expires.getTime() - Date.now()) / 86400000) : null);
  const status = job.status || (daysLeft !== null && daysLeft <= 0 ? "expired" : "active");

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`app-${APP_ID}-detail-dialog`}>
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-2xl font-heading tracking-tight leading-tight pr-4">{job.title}</DialogTitle>
            <StatusPill status={status} daysLeft={daysLeft} />
          </div>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5 mt-1">
          <Chip icon={Briefcase} label={job.category} />
          <Chip icon={MapPin} label={job.city} />
          <Chip icon={Clock} label={job.employment_type} />
          {job.work_mode && <Chip label={job.work_mode} />}
          {job.experience && <Chip label={job.experience} />}
          {job.company && <Chip icon={Building2} label={job.company} />}
        </div>
        {salary && (
          <div className="mt-3 flex items-center gap-1.5 text-emerald-700 font-medium text-sm">
            <Wallet className="h-4 w-4" /> {salary}
          </div>
        )}
        <div className="mt-4 text-sm whitespace-pre-wrap leading-relaxed text-foreground/85" data-testid={`app-${APP_ID}-detail-description`}>
          {job.description}
        </div>
        {job.languages && job.languages.length > 0 && (
          <div className="mt-3 text-xs">
            <span className="text-muted-foreground">Languages:</span> {job.languages.join(", ")}
          </div>
        )}
        <div className="mt-5 rounded-xl border border-primary/30 bg-primary/5 p-4">
          <div className="text-[11px] uppercase tracking-[0.15em] text-primary font-semibold mb-2">Reach out directly</div>
          <div className="text-xs text-muted-foreground mb-3">Posted by <strong className="text-foreground/90">{job.poster_name}</strong>{job.poster_city ? ` · ${job.poster_city}` : ""}</div>
          <div className="flex flex-wrap gap-2">
            {job.contact_phone && (
              <Button asChild size="sm" variant="outline" className="rounded-full h-8" data-testid={`app-${APP_ID}-contact-phone`}>
                <a href={`tel:${job.contact_phone}`}><Phone className="h-3.5 w-3.5 mr-1" /> {job.contact_phone}</a>
              </Button>
            )}
            {job.contact_whatsapp && (
              <Button asChild size="sm" className="rounded-full h-8 bg-green-600 hover:bg-green-700" data-testid={`app-${APP_ID}-contact-whatsapp`}>
                <a href={`https://wa.me/${job.contact_whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent("Hello, I'm reaching out about your " + job.title + " opening on sanskariBazarIn Rozgari")}`} target="_blank" rel="noreferrer">
                  <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                </a>
              </Button>
            )}
            {job.contact_email && (
              <Button asChild size="sm" variant="outline" className="rounded-full h-8" data-testid={`app-${APP_ID}-contact-email`}>
                <a href={`mailto:${job.contact_email}?subject=${encodeURIComponent("Re: " + job.title + " — sanskariBazarIn Rozgari")}`}><Mail className="h-3.5 w-3.5 mr-1" /> {job.contact_email}</a>
              </Button>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={share} className="rounded-full h-8" data-testid={`app-${APP_ID}-share-btn`}>
            <Share2 className="h-3.5 w-3.5 mr-1" /> Share
          </Button>
          <Button size="sm" variant="outline" onClick={copy} className="rounded-full h-8" data-testid={`app-${APP_ID}-copy-btn`}>
            <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
          </Button>
          <Button size="sm" variant="outline" onClick={whatsapp} className="rounded-full h-8 border-green-600/40 text-green-700" data-testid={`app-${APP_ID}-whatsapp-share-btn`}>
            <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp share
          </Button>
          {daysLeft !== null && daysLeft > 0 && status === "active" && (
            <span className="text-[11px] text-muted-foreground ml-auto">Expires in {daysLeft} day{daysLeft === 1 ? "" : "s"}</span>
          )}
          {status === "expired" && (
            <span className="text-[11px] text-orange-600 ml-auto">Auto-archived — post has expired</span>
          )}
        </div>

        {canManage && (
          <div className="mt-4 border-t border-border/60 pt-3 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">Renew for</span>
              {(meta?.expiry_options || [7, 14, 30, 60, 90]).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setRenewDays(d)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    renewDays === d
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-border/60 text-foreground/80 hover:bg-muted"
                  }`}
                  data-testid={`app-${APP_ID}-renew-days-${d}`}
                >
                  {d}d
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={renew} disabled={busy} className="rounded-full h-8" data-testid={`app-${APP_ID}-renew-btn`}>
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Renew {renewDays} days
              </Button>
              <Button size="sm" variant="ghost" onClick={remove} disabled={busy} className="rounded-full h-8 text-destructive hover:bg-destructive/10" data-testid={`app-${APP_ID}-delete-btn`}>
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* --------- My Jobs list --------- */

function MyJobsDialog({ onClose, onOpen }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/jobs/mine")
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid={`app-${APP_ID}-mine-dialog`}>
        <DialogHeader><DialogTitle>My job posts</DialogTitle></DialogHeader>
        {loading && <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-sm text-muted-foreground py-8 text-center">
            No posts yet. <button className="text-primary underline" onClick={onClose}>Post one</button> from the hero above.
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="space-y-2 mt-2">
            {items.map((j) => {
              const st = j.status || (j.expires_at && new Date(j.expires_at) < new Date() ? "expired" : "active");
              const stColor = st === "active"
                ? "bg-emerald-100 text-emerald-700"
                : st === "closed"
                  ? "bg-orange-100 text-orange-800"
                  : "bg-muted text-muted-foreground";
              const stLabel = st === "active" ? "Active" : st === "closed" ? "Closed" : "Expired";
              return (
                <button
                  key={j.job_id}
                  onClick={() => onOpen(j)}
                  className="w-full text-left rounded-lg border border-border/60 p-3 hover:border-primary/40 hover:bg-muted/40 transition"
                  data-testid={`app-${APP_ID}-mine-row-${j.job_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{j.title}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{j.category} · {j.city} · {j.employment_type}</div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${stColor}`}>{stLabel}</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1 flex gap-3">
                    <span>{j.views ?? 0} view{(j.views ?? 0) === 1 ? "" : "s"}</span>
                    {typeof j.days_left === "number" && (
                      <span>
                        {j.days_left > 0 ? `${j.days_left}d left` : `Expired ${Math.abs(j.days_left)}d ago`}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
